import {API} from '@/util/API';
import {AssetManager, SupportedChainId} from '@kei.fi/data';
import {FullTokenInfo} from '@/api-requests/tokens';
import {Asset} from '@/providers/overview/OverviewProvider';
import {PRIMARY_CHAIN} from '@/util/chains';

export type WithdrawalData = {
  to: string;
  data: string;
  baseAsset: string;
  receiveAmount: string;
};

export type HistoryItem = {
  maxBaseValue: string;
  currentBaseValue: string;
  totalProfit: string;
  profit: string;
  totalFees: string;
  fees: string;
  floatingBaseValue: string;
  floatingBaseVolume: string;
  shareValue: string;
  totalSupply: string;
  balances: {
    asset: string;
    balance: string;
    volume: string;
    price: string;
    floatingBaseValue: string;
  }[];
  timestamp: number;
};

export interface AssetManagerData extends AssetManager {
  accountBalances: {
    account: string;
    shares: string;
    percent: string;
  }[];
  maxBaseValue: string;
  currentBaseValue: string;
  totalProfit: string;
  totalFees: string;
  openPositions: {
    id: string;
    balance: string;
    baseValue: string;
    totalProfit: string;
    asset: string;
    symbol: string;
    decimals: number;
    createdAt: number;
  }[];
  closedPositions: {
    id: string;
    balance: string;
    baseValue: string;
    totalProfit: string;
    asset: string;
    symbol: string;
    decimals: number;
    createdAt: number;
  }[];
  contract: {
    address: string;
    tokenAddress: string;
    name: string;
    symbol: string;
    totalSupply: string;
    totalValueUSD: string;
    shareValueUSD: string;
    decimals: string;
  };
  accountTransactions: {
    account: string;
    amount: string;
    shares: string;
    timestamp: number;
    tx: string;
  }[];
  assetPositionsHourly: (Asset & {
    chartData: {price: number; balance: string}[];
  })[];
  assetPositionsDaily: (Asset & {
    chartData: {price: number; balance: string}[];
  })[];
  hourlyHistory: HistoryItem[];
  dailyHistory: HistoryItem[];
  assets: Record<
    string,
    FullTokenInfo & {symbol: string; decimals: string; deployment: string}
  >;
  assetBalances: Record<string, string>;
  trades: {
    id: string;
    fromAsset: string;
    fromSymbol: string;
    fromDecimals: number;
    fromAmount: string;
    toAsset: string;
    toSymbol: string;
    toDecimals: number;
    toAmount: string;
    profit: string | null;
    feeAmount: string;
    createdAt: number;
    tx: string;
  }[];
}

const getAssetManagers = async (
  chainId = PRIMARY_CHAIN
): Promise<AssetManagerData[]> => {
  const {assetManagers} = await API.get<
    null,
    {assetManagers: AssetManagerData[]}
  >(`/asset-manager/list?chainId=${chainId}`);
  return assetManagers;
};

const getWithdrawalData = async (
  assetManager: AssetManagerData,
  from: string,
  to: string,
  shares: string,
  chainId: SupportedChainId
): Promise<WithdrawalData> => {
  return API.post<null, WithdrawalData>('/asset-manager/create-withdrawal', {
    assetManagerId: assetManager.id,
    shares,
    from,
    to,
    chainId,
  });
};

export const assetManagersApi = {getAssetManagers, getWithdrawalData};
